import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form as RBForm, Modal } from 'react-bootstrap';
import styled from 'styled-components';
import { Card, CardBody, CardSubhead, CardTitle } from '@spark/ui-components/components/Card';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import TruncatedText from '@spark/ui-components/components/TanStackTable/components/TruncatedText';
import Tooltip from '@spark/ui-components/components/Tooltip';
import CheckBox from '@spark/ui-components/components/form/CheckBox';
import { handleNumberInputWheel } from '@spark/ui-components/components/form/numberInputUtils';
import { notification } from '@spark/ui-components/utils/notification';
import {
  deleteDepositoEnderecoEmLote,
  criarLayoutDepositoEndereco,
  createDepositoArmazem,
  createDepositoEndereco,
  deleteDepositoEndereco,
  getDepositoArmazenagemLocais,
  getDepositoArmazemById,
  getDepositoArmazens,
  getDepositoEnderecoById,
  getDepositoEnderecoMovimentacoes,
  getDepositoEnderecos,
  movimentarDepositoEnderecoNotas,
  updateDepositoArmazem,
  updateDepositoEndereco
} from '@spark/services/deposito';

const emptyArmazemForm = {
  selectedId: '',
  codigo: '',
  nome: '',
  imagemAtual: '',
  imagemBase64: '',
  imagemNomeArquivo: '',
  imagemContentType: '',
  imagemPreview: ''
};

const emptyEnderecoForm = {
  id: '',
  idDepositoArmazem: '',
  bloco: '',
  rua: '',
  nivel: '',
  posicao: ''
};

const emptyMovimentacaoForm = {
  enderecoOrigemTexto: '',
  enderecoDestinoTexto: '',
  idDepositoArmazenagemLocal: '',
  observacao: ''
};

const emptyLayoutForm = {
  idDepositoArmazem: '',
  bloco: '',
  quantidadeRuas: '',
  quantidadeNiveis: '',
  quantidadePosicoes: ''
};

const emptyHistoricoFilters = {
  idDepositoArmazemOrigem: '',
  idDepositoArmazemDestino: '',
  enderecoOrigemTexto: '',
  enderecoDestinoTexto: '',
  usuario: '',
  dataInicial: '',
  dataFinal: ''
};

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.message || fallback;

const parsePaginacao = (response) => {
  const data = response?.data || response || {};
  return {
    registros: Array.isArray(data.registros) ? data.registros : Array.isArray(data.Registros) ? data.Registros : [],
    totalRegistros: data.totalRegistros ?? data.TotalRegistros ?? 0
  };
};

const normalizeNumber = (value) => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const normalizePositiveInteger = (value) => {
  const parsed = normalizeNumber(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return undefined;
  return parsed;
};

const normalizeText = (value) => {
  if (value === null || value === undefined) return undefined;
  const normalized = String(value).trim();
  return normalized ? normalized : undefined;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('pt-BR');
};

const formatEnderecoLabel = (endereco) => {
  if (!endereco) return '';

  const armazem = endereco.armazem;
  const parts = [
    armazem?.codigo ? `Arm ${armazem.codigo}` : armazem?.nome || 'Sem armazem',
    endereco.bloco ? `Bloco ${endereco.bloco}` : null,
    endereco.rua !== null && endereco.rua !== undefined ? `Rua ${endereco.rua}` : null,
    endereco.nivel !== null && endereco.nivel !== undefined ? `Nivel ${endereco.nivel}` : null,
    endereco.posicao !== null && endereco.posicao !== undefined ? `Posicao ${endereco.posicao}` : null
  ].filter(Boolean);

  return parts.join(' / ') || `Endereco ${endereco.id}`;
};

const normalizeEnderecoSegment = (value) => (value === null || value === undefined ? '' : String(value).trim().toUpperCase());

const parseEnderecoTexto = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) return null;

  const parts = normalized.split('.');
  if (parts.length !== 5) return null;

  const rua = Number(parts[2]);
  const nivel = Number(parts[3]);
  const posicao = Number(parts[4]);

  if (!Number.isInteger(rua) || rua <= 0) return null;
  if (!Number.isInteger(nivel) || nivel <= 0) return null;
  if (!Number.isInteger(posicao) || posicao <= 0) return null;

  return {
    codigoArmazem: normalizeEnderecoSegment(parts[0]),
    bloco: normalizeEnderecoSegment(parts[1]),
    rua,
    nivel,
    posicao
  };
};

const findEnderecoByTexto = (lookup, enderecoTexto) => {
  const parsed = parseEnderecoTexto(enderecoTexto);
  if (!parsed) return null;

  const matches = (Array.isArray(lookup) ? lookup : []).filter((item) => (
    normalizeEnderecoSegment(item?.armazem?.codigo) === parsed.codigoArmazem
    && normalizeEnderecoSegment(item?.bloco) === parsed.bloco
    && Number(item?.rua) === parsed.rua
    && Number(item?.nivel) === parsed.nivel
    && Number(item?.posicao) === parsed.posicao
  ));

  return matches.length === 1 ? matches[0] : null;
};

const normalizeExclusaoLoteResumo = (response) => {
  const data = response?.data || response || {};
  const itensRaw = Array.isArray(data.itens)
    ? data.itens
    : Array.isArray(data.Itens)
      ? data.Itens
      : [];

  return {
    quantidadeSolicitada: data.quantidadeSolicitada ?? data.QuantidadeSolicitada ?? 0,
    quantidadeProcessada: data.quantidadeProcessada ?? data.QuantidadeProcessada ?? itensRaw.length,
    quantidadeSucesso: data.quantidadeSucesso ?? data.QuantidadeSucesso ?? 0,
    quantidadeErro: data.quantidadeErro ?? data.QuantidadeErro ?? 0,
    itens: itensRaw.map((item) => ({
      id: item.id ?? item.Id,
      descricao: item.descricao ?? item.Descricao ?? '',
      sucesso: item.sucesso ?? item.Sucesso ?? false,
      mensagem: item.mensagem ?? item.Mensagem ?? ''
    }))
  };
};

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = (error) => reject(error);
  reader.readAsDataURL(file);
});

const EnderecamentoCard = () => {
  const [filters, setFilters] = useState({});
  const [paginationState, setPaginationState] = useState({ pageIndex: 0, pageSize: 10 });
  const [enderecos, setEnderecos] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [loading, setLoading] = useState(false);

  const [armazens, setArmazens] = useState([]);
  const [enderecosLookup, setEnderecosLookup] = useState([]);
  const [armazenagemLocais, setArmazenagemLocais] = useState([]);
  const [loadingLookup, setLoadingLookup] = useState(false);

  const [armazemModalOpen, setArmazemModalOpen] = useState(false);
  const [enderecoModalOpen, setEnderecoModalOpen] = useState(false);
  const [layoutModalOpen, setLayoutModalOpen] = useState(false);
  const [movimentacaoModalOpen, setMovimentacaoModalOpen] = useState(false);
  const [historicoModalOpen, setHistoricoModalOpen] = useState(false);

  const [armazemForm, setArmazemForm] = useState(emptyArmazemForm);
  const [enderecoForm, setEnderecoForm] = useState(emptyEnderecoForm);
  const [layoutForm, setLayoutForm] = useState(emptyLayoutForm);
  const [movimentacaoForm, setMovimentacaoForm] = useState(emptyMovimentacaoForm);
  const [historicoFilters, setHistoricoFilters] = useState(emptyHistoricoFilters);
  const [historicoMovimentacoes, setHistoricoMovimentacoes] = useState([]);
  const [selectedHistoricoId, setSelectedHistoricoId] = useState(null);
  const [selectedEnderecos, setSelectedEnderecos] = useState([]);
  const [exclusaoLoteResumo, setExclusaoLoteResumo] = useState(null);
  const [exclusaoLoteResumoOpen, setExclusaoLoteResumoOpen] = useState(false);

  const [salvandoArmazem, setSalvandoArmazem] = useState(false);
  const [salvandoEndereco, setSalvandoEndereco] = useState(false);
  const [criandoLayout, setCriandoLayout] = useState(false);
  const [removendoEnderecoId, setRemovendoEnderecoId] = useState(null);
  const [removendoEnderecosLote, setRemovendoEnderecosLote] = useState(false);
  const [movimentando, setMovimentando] = useState(false);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [loadingArmazemDetails, setLoadingArmazemDetails] = useState(false);
  const [loadingEnderecoDetails, setLoadingEnderecoDetails] = useState(false);

  const carregarArmazens = useCallback(async () => {
    try {
      const response = await getDepositoArmazens({
        PaginaAtual: 1,
        QuantidadePorPagina: 200
      });
      const { registros } = parsePaginacao(response);
      setArmazens(registros);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao carregar armazens'));
    }
  }, []);

  const carregarEnderecosLookup = useCallback(async () => {
    setLoadingLookup(true);
    try {
      const response = await getDepositoEnderecos({
        PaginaAtual: 1,
        QuantidadePorPagina: 1000
      });
      const { registros } = parsePaginacao(response);
      setEnderecosLookup(registros);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao carregar enderecos'));
    } finally {
      setLoadingLookup(false);
    }
  }, []);

  const carregarArmazenagemLocais = useCallback(async () => {
    try {
      const response = await getDepositoArmazenagemLocais();
      const registros = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setArmazenagemLocais(registros);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao carregar locais de armazenagem'));
    }
  }, []);

  const carregarEnderecos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDepositoEnderecos({
        ...filters,
        PaginaAtual: (paginationState?.pageIndex || 0) + 1,
        QuantidadePorPagina: paginationState?.pageSize || 10
      });
      const { registros, totalRegistros: total } = parsePaginacao(response);
      setEnderecos(registros);
      setTotalRegistros(total);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao carregar enderecos'));
    } finally {
      setLoading(false);
    }
  }, [filters, paginationState]);

  const carregarHistorico = useCallback(async () => {
    setLoadingHistorico(true);
    try {
      const response = await getDepositoEnderecoMovimentacoes({
        ...historicoFilters,
        PaginaAtual: 1,
        QuantidadePorPagina: 100
      });
      const { registros } = parsePaginacao(response);
      setHistoricoMovimentacoes(registros);
      setSelectedHistoricoId((current) => {
        if (current && registros.some((item) => item.id === current)) return current;
        return registros[0]?.id || null;
      });
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao carregar movimentacoes'));
    } finally {
      setLoadingHistorico(false);
    }
  }, [historicoFilters]);

  useEffect(() => {
    carregarArmazens();
    carregarEnderecosLookup();
    carregarArmazenagemLocais();
  }, [carregarArmazens, carregarArmazenagemLocais, carregarEnderecosLookup]);

  useEffect(() => {
    carregarEnderecos();
  }, [carregarEnderecos]);

  const armazemOptions = useMemo(
    () => armazens.map((item) => ({
      value: String(item.id),
      label: `${item.codigo || '-'} - ${item.nome || 'Sem nome'}`
    })),
    [armazens]
  );

  const armazenagemLocalOptions = useMemo(
    () => armazenagemLocais.map((item) => ({
      value: String(item.id),
      label: `${item.codigo} - ${item.descricao || 'Sem descricao'}`
    })),
    [armazenagemLocais]
  );

  const filterFields = useMemo(
    () => [
      {
        id: 'IdDepositoArmazem',
        type: 'select',
        label: 'Armazem',
        placeholder: 'Selecione...',
        options: armazemOptions
      },
      {
        id: 'NumeroNF',
        type: 'text',
        label: 'Nota Fiscal',
        placeholder: 'Digite a nota fiscal'
      },
      {
        id: 'CNPJCliente',
        type: 'text',
        label: 'CNPJ Cliente',
        placeholder: 'Digite o CNPJ cliente'
      },
      {
        id: 'Bloco',
        type: 'text',
        label: 'Bloco',
        placeholder: 'Digite o bloco'
      },
      {
        id: 'Rua',
        type: 'text',
        label: 'Rua',
        placeholder: 'Digite a rua'
      },
      {
        id: 'Nivel',
        type: 'text',
        label: 'Nivel',
        placeholder: 'Digite o nivel'
      },
      {
        id: 'Posicao',
        type: 'text',
        label: 'Posicao',
        placeholder: 'Digite a posicao'
      }
    ],
    [armazemOptions]
  );

  const selectedHistorico = useMemo(
    () => historicoMovimentacoes.find((item) => item.id === selectedHistoricoId) || null,
    [historicoMovimentacoes, selectedHistoricoId]
  );

  const selectedEnderecoOrigem = useMemo(
    () => findEnderecoByTexto(enderecosLookup, movimentacaoForm.enderecoOrigemTexto),
    [enderecosLookup, movimentacaoForm.enderecoOrigemTexto]
  );

  const selectedEnderecoIds = useMemo(
    () => selectedEnderecos.map((item) => item.id).filter(Boolean),
    [selectedEnderecos]
  );

  const allPageSelected = useMemo(
    () => enderecos.length > 0 && enderecos.every((item) => selectedEnderecoIds.includes(item.id)),
    [enderecos, selectedEnderecoIds]
  );

  const clearSelectedEnderecos = useCallback(() => {
    setSelectedEnderecos([]);
  }, []);

  const handleApplyFilters = useCallback((appliedFilters) => {
    setFilters(appliedFilters || {});
    clearSelectedEnderecos();
    setPaginationState((current) => ({ ...current, pageIndex: 0 }));
  }, [clearSelectedEnderecos]);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    clearSelectedEnderecos();
    setPaginationState((current) => ({ ...current, pageIndex: 0 }));
  }, [clearSelectedEnderecos]);

  const resetArmazemForm = useCallback(() => {
    setArmazemForm(emptyArmazemForm);
  }, []);

  const resetEnderecoForm = useCallback(() => {
    setEnderecoForm(emptyEnderecoForm);
  }, []);

  const resetLayoutForm = useCallback(() => {
    setLayoutForm(emptyLayoutForm);
  }, []);

  const resetMovimentacaoForm = useCallback(() => {
    setMovimentacaoForm(emptyMovimentacaoForm);
  }, []);

  const handleOpenArmazemModal = useCallback(() => {
    resetArmazemForm();
    setArmazemModalOpen(true);
  }, [resetArmazemForm]);

  const handleCloseArmazemModal = useCallback(() => {
    setArmazemModalOpen(false);
    resetArmazemForm();
  }, [resetArmazemForm]);

  const handleOpenEnderecoModal = useCallback(async (id) => {
    if (!id) {
      resetEnderecoForm();
      setEnderecoModalOpen(true);
      return;
    }

    setLoadingEnderecoDetails(true);
    try {
      const response = await getDepositoEnderecoById(id);
      setEnderecoForm({
        id: response?.id || '',
        idDepositoArmazem: response?.idDepositoArmazem ? String(response.idDepositoArmazem) : '',
        bloco: response?.bloco || '',
        rua: response?.rua ?? '',
        nivel: response?.nivel ?? '',
        posicao: response?.posicao ?? ''
      });
      setEnderecoModalOpen(true);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao carregar endereco'));
    } finally {
      setLoadingEnderecoDetails(false);
    }
  }, [resetEnderecoForm]);

  const handleCloseEnderecoModal = useCallback(() => {
    setEnderecoModalOpen(false);
    resetEnderecoForm();
  }, [resetEnderecoForm]);

  const handleOpenLayoutModal = useCallback(() => {
    resetLayoutForm();
    setLayoutModalOpen(true);
  }, [resetLayoutForm]);

  const handleCloseLayoutModal = useCallback(() => {
    setLayoutModalOpen(false);
    resetLayoutForm();
  }, [resetLayoutForm]);

  const handleOpenMovimentacaoModal = useCallback(() => {
    resetMovimentacaoForm();
    setMovimentacaoModalOpen(true);
  }, [resetMovimentacaoForm]);

  const handleCloseMovimentacaoModal = useCallback(() => {
    setMovimentacaoModalOpen(false);
    resetMovimentacaoForm();
  }, [resetMovimentacaoForm]);

  const handleOpenHistoricoModal = useCallback(() => {
    setHistoricoModalOpen(true);
    carregarHistorico();
  }, [carregarHistorico]);

  const handleCloseHistoricoModal = useCallback(() => {
    setHistoricoModalOpen(false);
    setSelectedHistoricoId(null);
  }, []);

  const handleArmazemSelectionChange = useCallback(async (selectedId) => {
    if (!selectedId) {
      resetArmazemForm();
      return;
    }

    setLoadingArmazemDetails(true);
    try {
      const response = await getDepositoArmazemById(selectedId);
      setArmazemForm({
        selectedId: String(response?.id || ''),
        codigo: response?.codigo || '',
        nome: response?.nome || '',
        imagemAtual: response?.imagem || '',
        imagemBase64: '',
        imagemNomeArquivo: '',
        imagemContentType: '',
        imagemPreview: response?.imagem || ''
      });
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao carregar armazem'));
    } finally {
      setLoadingArmazemDetails(false);
    }
  }, [resetArmazemForm]);

  const handleArmazemImageChange = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setArmazemForm((current) => ({
        ...current,
        imagemBase64: '',
        imagemNomeArquivo: '',
        imagemContentType: '',
        imagemPreview: current.imagemAtual || ''
      }));
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      const base64 = dataUrl.includes(',') ? dataUrl.split(',').pop() : dataUrl;

      setArmazemForm((current) => ({
        ...current,
        imagemBase64: base64 || '',
        imagemNomeArquivo: file.name,
        imagemContentType: file.type || 'application/octet-stream',
        imagemPreview: dataUrl
      }));
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao ler imagem'));
    } finally {
      event.target.value = '';
    }
  }, []);

  const handleSubmitArmazem = useCallback(async () => {
    if (!normalizeText(armazemForm.codigo)) {
      notification.warning('Informe o codigo do armazem.');
      return;
    }

    if (!normalizeText(armazemForm.nome)) {
      notification.warning('Informe o nome do armazem.');
      return;
    }

    const payload = {
      Codigo: normalizeText(armazemForm.codigo),
      Nome: normalizeText(armazemForm.nome)
    };

    if (armazemForm.imagemBase64) {
      payload.ImagemBase64 = armazemForm.imagemBase64;
      payload.ImagemNomeArquivo = armazemForm.imagemNomeArquivo;
      payload.ImagemContentType = armazemForm.imagemContentType;
    }

    setSalvandoArmazem(true);
    try {
      if (armazemForm.selectedId) {
        await updateDepositoArmazem(armazemForm.selectedId, payload);
        notification.success('Armazem atualizado com sucesso!');
      } else {
        await createDepositoArmazem(payload);
        notification.success('Armazem criado com sucesso!');
      }

      handleCloseArmazemModal();
      await Promise.all([carregarArmazens(), carregarEnderecosLookup(), carregarEnderecos()]);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao salvar armazem'));
    } finally {
      setSalvandoArmazem(false);
    }
  }, [armazemForm, carregarArmazens, carregarEnderecos, carregarEnderecosLookup, handleCloseArmazemModal]);

  const handleSubmitEndereco = useCallback(async () => {
    const payload = {
      IdDepositoArmazem: normalizeNumber(enderecoForm.idDepositoArmazem),
      Bloco: normalizeText(enderecoForm.bloco),
      Rua: normalizeNumber(enderecoForm.rua),
      Nivel: normalizeNumber(enderecoForm.nivel),
      Posicao: normalizeNumber(enderecoForm.posicao)
    };

    setSalvandoEndereco(true);
    try {
      if (enderecoForm.id) {
        await updateDepositoEndereco(enderecoForm.id, payload);
        notification.success('Endereco atualizado com sucesso!');
      } else {
        await createDepositoEndereco(payload);
        notification.success('Endereco criado com sucesso!');
      }

      handleCloseEnderecoModal();
      await Promise.all([carregarEnderecos(), carregarEnderecosLookup()]);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao salvar endereco'));
    } finally {
      setSalvandoEndereco(false);
    }
  }, [carregarEnderecos, carregarEnderecosLookup, enderecoForm, handleCloseEnderecoModal]);

  const handleSubmitLayout = useCallback(async () => {
    if (!layoutForm.idDepositoArmazem) {
      notification.warning('Selecione o armazem do layout.');
      return;
    }

    const quantidadeRuas = normalizePositiveInteger(layoutForm.quantidadeRuas);
    const quantidadeNiveis = normalizePositiveInteger(layoutForm.quantidadeNiveis);
    const quantidadePosicoes = normalizePositiveInteger(layoutForm.quantidadePosicoes);

    if (!quantidadeRuas) {
      notification.warning('Informe uma quantidade valida de ruas.');
      return;
    }

    if (!quantidadeNiveis) {
      notification.warning('Informe uma quantidade valida de niveis.');
      return;
    }

    if (!quantidadePosicoes) {
      notification.warning('Informe uma quantidade valida de posicoes.');
      return;
    }

    setCriandoLayout(true);
    try {
      const response = await criarLayoutDepositoEndereco({
        IdDepositoArmazem: Number(layoutForm.idDepositoArmazem),
        Bloco: normalizeText(layoutForm.bloco),
        QuantidadeRuas: quantidadeRuas,
        QuantidadeNiveis: quantidadeNiveis,
        QuantidadePosicoes: quantidadePosicoes
      });

      notification.success(`Layout criado com ${response?.quantidadeCriada || 0} endereco(s).`);
      handleCloseLayoutModal();
      await Promise.all([carregarEnderecos(), carregarEnderecosLookup()]);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao criar layout de enderecos'));
    } finally {
      setCriandoLayout(false);
    }
  }, [carregarEnderecos, carregarEnderecosLookup, handleCloseLayoutModal, layoutForm]);

  const handleDeleteEndereco = useCallback(async (endereco) => {
    if (!endereco?.id) return;

    const confirmacao = window.confirm(`Deseja realmente excluir o endereco ${formatEnderecoLabel(endereco)}?`);
    if (!confirmacao) return;

    setRemovendoEnderecoId(endereco.id);
    try {
      await deleteDepositoEndereco(endereco.id);
      notification.success('Endereco excluido com sucesso!');
      setSelectedEnderecos((current) => current.filter((item) => item.id !== endereco.id));
      await Promise.all([carregarEnderecos(), carregarEnderecosLookup()]);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao excluir endereco'));
    } finally {
      setRemovendoEnderecoId(null);
    }
  }, [carregarEnderecos, carregarEnderecosLookup]);

  const handleToggleSelectEndereco = useCallback((row) => {
    if (!row?.id) return;

    setSelectedEnderecos((current) => {
      if (current.some((item) => item.id === row.id)) {
        return current.filter((item) => item.id !== row.id);
      }

      return [...current, row];
    });
  }, []);

  const handleToggleSelectAllEnderecos = useCallback(() => {
    if (!enderecos.length) return;

    setSelectedEnderecos((current) => {
      const allSelected = enderecos.every((item) => current.some((selected) => selected.id === item.id));
      if (allSelected) {
        return current.filter((selected) => !enderecos.some((item) => item.id === selected.id));
      }

      const novos = enderecos.filter((item) => !current.some((selected) => selected.id === item.id));
      return [...current, ...novos];
    });
  }, [enderecos]);

  const handleCloseExclusaoLoteResumoModal = useCallback(() => {
    setExclusaoLoteResumoOpen(false);
    setExclusaoLoteResumo(null);
  }, []);

  const handleDeleteEnderecosSelecionados = useCallback(async () => {
    if (!selectedEnderecoIds.length) return;

    const confirmacao = window.confirm(`Deseja realmente excluir ${selectedEnderecoIds.length} endereco(s) selecionado(s)?`);
    if (!confirmacao) return;

    setRemovendoEnderecosLote(true);
    try {
      const response = await deleteDepositoEnderecoEmLote({ Ids: selectedEnderecoIds });
      const resumo = normalizeExclusaoLoteResumo(response);
      setExclusaoLoteResumo(resumo);
      setExclusaoLoteResumoOpen(true);
      clearSelectedEnderecos();
      await Promise.all([carregarEnderecos(), carregarEnderecosLookup()]);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao excluir enderecos em lote'));
    } finally {
      setRemovendoEnderecosLote(false);
    }
  }, [carregarEnderecos, carregarEnderecosLookup, clearSelectedEnderecos, selectedEnderecoIds]);

  const handleSubmitMovimentacao = useCallback(async () => {
    const enderecoOrigemTexto = normalizeText(movimentacaoForm.enderecoOrigemTexto);
    const enderecoDestinoTexto = normalizeText(movimentacaoForm.enderecoDestinoTexto);

    if (!enderecoOrigemTexto) {
      notification.warning('Informe o endereco de origem.');
      return;
    }

    if (!enderecoDestinoTexto) {
      notification.warning('Informe o endereco de destino.');
      return;
    }

    if (normalizeEnderecoSegment(enderecoOrigemTexto) === normalizeEnderecoSegment(enderecoDestinoTexto)) {
      notification.warning('Origem e destino devem ser diferentes.');
      return;
    }

    if (!movimentacaoForm.idDepositoArmazenagemLocal) {
      notification.warning('Selecione o local de armazenagem.');
      return;
    }

    setMovimentando(true);
    try {
      const response = await movimentarDepositoEnderecoNotas({
        EnderecoOrigemTexto: enderecoOrigemTexto,
        EnderecoDestinoTexto: enderecoDestinoTexto,
        IdDepositoArmazenagemLocal: Number(movimentacaoForm.idDepositoArmazenagemLocal),
        Observacao: normalizeText(movimentacaoForm.observacao)
      });

      notification.success(`Movimentacao concluida com ${response?.quantidadeNotas || 0} nota(s).`);
      handleCloseMovimentacaoModal();
      await Promise.all([carregarEnderecos(), carregarEnderecosLookup(), carregarHistorico()]);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao movimentar notas entre enderecos'));
    } finally {
      setMovimentando(false);
    }
  }, [carregarEnderecos, carregarEnderecosLookup, carregarHistorico, handleCloseMovimentacaoModal, movimentacaoForm]);

  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: () => (
          <Tooltip dir="top" text={allPageSelected ? 'Remover selecao da pagina' : 'Selecionar pagina'}>
            <span className="d-inline-flex align-items-center">
              <CheckBox
                name="select-all-enderecos"
                checked={!!allPageSelected}
                disabled={!enderecos.length}
                label=""
                onChange={(event) => {
                  event.stopPropagation();
                  handleToggleSelectAllEnderecos();
                }}
                onClick={(event) => event.stopPropagation()}
              />
            </span>
          </Tooltip>
        ),
        size: 50,
        enableSorting: false,
        cell: ({ row }) => (
          <Tooltip dir="top" text={selectedEnderecoIds.includes(row.original.id) ? 'Remover selecao' : 'Selecionar'}>
            <span className="d-inline-flex align-items-center">
              <CheckBox
                name={`select-endereco-${row.original.id}`}
                checked={selectedEnderecoIds.includes(row.original.id)}
                label=""
                onChange={(event) => {
                  event.stopPropagation();
                  handleToggleSelectEndereco(row.original);
                }}
                onClick={(event) => event.stopPropagation()}
              />
            </span>
          </Tooltip>
        )
      },
      {
        id: 'id',
        header: 'Id',
        accessorKey: 'id',
        size: 70,
        cell: ({ getValue }) => <span>{getValue()}</span>
      },
      {
        id: 'armazem',
        header: 'Armazem',
        accessorFn: (row) => row.armazem?.nome || '-',
        size: 180,
        cell: ({ row }) => {
          const armazem = row.original.armazem;
          const value = armazem ? `${armazem.codigo || '-'} - ${armazem.nome || '-'}` : '-';
          return <TruncatedText value={value} maxLength={28} />;
        }
      },
      {
        id: 'bloco',
        header: 'Bloco',
        accessorKey: 'bloco',
        size: 110,
        cell: ({ getValue }) => <span>{getValue() || '-'}</span>
      },
      {
        id: 'rua',
        header: 'Rua',
        accessorKey: 'rua',
        size: 90,
        cell: ({ getValue }) => <span>{getValue() ?? '-'}</span>
      },
      {
        id: 'nivel',
        header: 'Nivel',
        accessorKey: 'nivel',
        size: 90,
        cell: ({ getValue }) => <span>{getValue() ?? '-'}</span>
      },
      {
        id: 'posicao',
        header: 'Posicao',
        accessorKey: 'posicao',
        size: 100,
        cell: ({ getValue }) => <span>{getValue() ?? '-'}</span>
      },
      {
        id: 'quantidadeNotas',
        header: 'Qtde Notas',
        accessorKey: 'quantidadeNotas',
        size: 120,
        cell: ({ getValue }) => <span>{getValue() ?? 0}</span>
      },
      {
        id: 'acoes',
        header: 'Acoes',
        size: 220,
        enableSorting: false,
        cell: ({ row }) => (
          <ActionButtonsWrap>
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenEnderecoModal(row.original.id);
              }}
            >
              Editar
            </Button>
            <ButtonWithLoading
              type="button"
              variant="outline-danger"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                handleDeleteEndereco(row.original);
              }}
              loading={removendoEnderecoId === row.original.id}
            >
              Excluir
            </ButtonWithLoading>
          </ActionButtonsWrap>
        )
      }
    ],
    [allPageSelected, enderecos.length, handleDeleteEndereco, handleOpenEnderecoModal, handleToggleSelectAllEnderecos, handleToggleSelectEndereco, removendoEnderecoId, selectedEnderecoIds]
  );

  return (
    <Card>
      <CardBody>
        <HeaderWrap>
          <div>
            <CardTitle className="mb-0">Enderecamento</CardTitle>
            <CardSubhead>Total de registros: {totalRegistros}</CardSubhead>
          </div>
          <ActionsWrap>
            <ButtonWithLoading
              type="button"
              variant="outline-danger"
              onClick={handleDeleteEnderecosSelecionados}
              loading={removendoEnderecosLote}
              disabled={!selectedEnderecoIds.length}
            >
              Excluir
            </ButtonWithLoading>
            <ButtonWithLoading
              type="button"
              variant="outline-secondary"
              onClick={handleOpenArmazemModal}
              loading={loadingArmazemDetails}
            >
              Armazem
            </ButtonWithLoading>
            <Button
              type="button"
              variant="outline-primary"
              onClick={() => handleOpenEnderecoModal()}
            >
              Endereco
            </Button>
            <Button
              type="button"
              variant="outline-success"
              onClick={handleOpenLayoutModal}
            >
              Criar Layout
            </Button>
            <ButtonWithLoading
              type="button"
              variant="outline-warning"
              onClick={handleOpenMovimentacaoModal}
              loading={loadingLookup}
            >
              Movimentacao
            </ButtonWithLoading>
            <ButtonWithLoading
              type="button"
              variant="primary"
              onClick={handleOpenHistoricoModal}
              loading={loadingHistorico}
            >
              Historico
            </ButtonWithLoading>
          </ActionsWrap>
        </HeaderWrap>

        <FilterPanel
          filters={filterFields}
          onApply={handleApplyFilters}
          onLimpar={handleClearFilters}
          title="Filtros de Enderecamento"
          loading={loading}
          defaultCollapsed={true}
        />

        <TanStackTable
          columns={columns}
          data={enderecos}
          isSortable={true}
          withPagination={true}
          manualPagination={true}
          totalItems={totalRegistros}
          onPaginationChange={setPaginationState}
          controlledPagination={paginationState}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          initialState={{
            pageIndex: paginationState?.pageIndex || 0,
            pageSize: paginationState?.pageSize || 10
          }}
          enableColumnConfig={true}
          columnConfigKey="deposito-enderecamento-columns"
          loading={loading}
          className="mt-3"
        />

        <Modal show={armazemModalOpen} onHide={handleCloseArmazemModal} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>{armazemForm.selectedId ? 'Atualizar Armazem' : 'Criar Armazem'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <RBForm.Group className="mb-3">
              <RBForm.Label>Armazem existente</RBForm.Label>
              <RBForm.Select
                value={armazemForm.selectedId}
                onChange={(event) => handleArmazemSelectionChange(event.target.value)}
                disabled={loadingArmazemDetails}
              >
                <option value="">Novo armazem</option>
                {armazemOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </RBForm.Select>
            </RBForm.Group>

            <TwoColumns>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Codigo</RBForm.Label>
                <RBForm.Control
                  value={armazemForm.codigo}
                  onChange={(event) => setArmazemForm((current) => ({ ...current, codigo: event.target.value }))}
                />
              </RBForm.Group>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Nome</RBForm.Label>
                <RBForm.Control
                  value={armazemForm.nome}
                  onChange={(event) => setArmazemForm((current) => ({ ...current, nome: event.target.value }))}
                />
              </RBForm.Group>
            </TwoColumns>

            <RBForm.Group className="mb-3">
              <RBForm.Label>Imagem</RBForm.Label>
              <RBForm.Control type="file" accept="image/*" onChange={handleArmazemImageChange} />
              <RBForm.Text muted>A imagem sera enviada ao bucket da AWS quando voce salvar.</RBForm.Text>
            </RBForm.Group>

            {armazemForm.imagemPreview && (
              <PreviewWrap>
                <PreviewLabel>Preview</PreviewLabel>
                <PreviewImage src={armazemForm.imagemPreview} alt="Preview do armazem" />
                <PreviewLink href={armazemForm.imagemPreview} target="_blank" rel="noopener noreferrer">
                  Abrir imagem
                </PreviewLink>
              </PreviewWrap>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleCloseArmazemModal}>
              Cancelar
            </Button>
            <ButtonWithLoading type="button" variant="primary" onClick={handleSubmitArmazem} loading={salvandoArmazem}>
              Salvar
            </ButtonWithLoading>
          </Modal.Footer>
        </Modal>

        <Modal show={enderecoModalOpen} onHide={handleCloseEnderecoModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{enderecoForm.id ? 'Atualizar Endereco' : 'Criar Endereco'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <RBForm.Group className="mb-3">
              <RBForm.Label>Armazem</RBForm.Label>
              <RBForm.Select
                value={enderecoForm.idDepositoArmazem}
                onChange={(event) => setEnderecoForm((current) => ({ ...current, idDepositoArmazem: event.target.value }))}
                disabled={loadingEnderecoDetails}
              >
                <option value="">Sem armazem</option>
                {armazemOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </RBForm.Select>
            </RBForm.Group>

            <TwoColumns>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Bloco</RBForm.Label>
                <RBForm.Control
                  value={enderecoForm.bloco}
                  onChange={(event) => setEnderecoForm((current) => ({ ...current, bloco: event.target.value }))}
                />
              </RBForm.Group>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Rua</RBForm.Label>
                <RBForm.Control
                  value={enderecoForm.rua}
                  onChange={(event) => setEnderecoForm((current) => ({ ...current, rua: event.target.value }))}
                />
              </RBForm.Group>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Nivel</RBForm.Label>
                <RBForm.Control
                  value={enderecoForm.nivel}
                  onChange={(event) => setEnderecoForm((current) => ({ ...current, nivel: event.target.value }))}
                />
              </RBForm.Group>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Posicao</RBForm.Label>
                <RBForm.Control
                  value={enderecoForm.posicao}
                  onChange={(event) => setEnderecoForm((current) => ({ ...current, posicao: event.target.value }))}
                />
              </RBForm.Group>
            </TwoColumns>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleCloseEnderecoModal}>
              Cancelar
            </Button>
            <ButtonWithLoading type="button" variant="primary" onClick={handleSubmitEndereco} loading={salvandoEndereco}>
              Salvar
            </ButtonWithLoading>
          </Modal.Footer>
        </Modal>

        <Modal show={layoutModalOpen} onHide={handleCloseLayoutModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Criar Layout</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <RBForm.Group className="mb-3">
              <RBForm.Label>Armazem</RBForm.Label>
              <RBForm.Select
                value={layoutForm.idDepositoArmazem}
                onChange={(event) => setLayoutForm((current) => ({ ...current, idDepositoArmazem: event.target.value }))}
              >
                <option value="">Selecione...</option>
                {armazemOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </RBForm.Select>
            </RBForm.Group>

            <RBForm.Group className="mb-3">
              <RBForm.Label>Bloco</RBForm.Label>
              <RBForm.Control
                value={layoutForm.bloco}
                onChange={(event) => setLayoutForm((current) => ({ ...current, bloco: event.target.value }))}
              />
            </RBForm.Group>

            <TwoColumns>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Quantidade de ruas</RBForm.Label>
                <RBForm.Control
                  type="number"
                  min={1}
                  step={1}
                  value={layoutForm.quantidadeRuas}
                  onChange={(event) => setLayoutForm((current) => ({ ...current, quantidadeRuas: event.target.value }))}
                  onWheel={handleNumberInputWheel}
                />
              </RBForm.Group>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Quantidade de niveis</RBForm.Label>
                <RBForm.Control
                  type="number"
                  min={1}
                  step={1}
                  value={layoutForm.quantidadeNiveis}
                  onChange={(event) => setLayoutForm((current) => ({ ...current, quantidadeNiveis: event.target.value }))}
                  onWheel={handleNumberInputWheel}
                />
              </RBForm.Group>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Quantidade de posicoes</RBForm.Label>
                <RBForm.Control
                  type="number"
                  min={1}
                  step={1}
                  value={layoutForm.quantidadePosicoes}
                  onChange={(event) => setLayoutForm((current) => ({ ...current, quantidadePosicoes: event.target.value }))}
                  onWheel={handleNumberInputWheel}
                />
              </RBForm.Group>
            </TwoColumns>

            <InfoBox>
              <strong>Regra do layout:</strong> serao criadas todas as combinacoes de rua, nivel e posicao para o armazem selecionado.
              <InfoText>Se qualquer endereco do lote ja existir nesse armazem, nenhuma linha sera criada.</InfoText>
            </InfoBox>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleCloseLayoutModal}>
              Cancelar
            </Button>
            <ButtonWithLoading type="button" variant="success" onClick={handleSubmitLayout} loading={criandoLayout}>
              Criar layout
            </ButtonWithLoading>
          </Modal.Footer>
        </Modal>

        <Modal show={movimentacaoModalOpen} onHide={handleCloseMovimentacaoModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Movimentar Notas entre Enderecos</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <InfoBox className="mb-3">
              <strong>Endereco: 1.A.2.3.4</strong>
              <InfoText>1 = Armazem</InfoText>
              <InfoText>A = Bloco</InfoText>
              <InfoText>2 = Rua</InfoText>
              <InfoText>3 = Nivel</InfoText>
              <InfoText>4 = Posicao</InfoText>
              <InfoText>Sem bloco: 1..2.3.4</InfoText>
            </InfoBox>

            <RBForm.Group className="mb-3">
              <RBForm.Label>Endereco origem</RBForm.Label>
              <RBForm.Control
                value={movimentacaoForm.enderecoOrigemTexto}
                onChange={(event) => setMovimentacaoForm((current) => ({
                  ...current,
                  enderecoOrigemTexto: event.target.value
                }))}
                placeholder="Ex: 1.A.2.3.4"
              />
            </RBForm.Group>

            <RBForm.Group className="mb-3">
              <RBForm.Label>Endereco destino</RBForm.Label>
              <RBForm.Control
                value={movimentacaoForm.enderecoDestinoTexto}
                onChange={(event) => setMovimentacaoForm((current) => ({
                  ...current,
                  enderecoDestinoTexto: event.target.value
                }))}
                placeholder="Ex: 1..2.3.4"
              />
            </RBForm.Group>

            <RBForm.Group className="mb-3">
              <RBForm.Label>Local de armazenagem</RBForm.Label>
              <RBForm.Select
                value={movimentacaoForm.idDepositoArmazenagemLocal}
                onChange={(event) => setMovimentacaoForm((current) => ({
                  ...current,
                  idDepositoArmazenagemLocal: event.target.value
                }))}
              >
                <option value="">Selecione...</option>
                {armazenagemLocalOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </RBForm.Select>
            </RBForm.Group>

            <RBForm.Group className="mb-3">
              <RBForm.Label>Observacao</RBForm.Label>
              <RBForm.Control
                as="textarea"
                rows={3}
                value={movimentacaoForm.observacao}
                onChange={(event) => setMovimentacaoForm((current) => ({ ...current, observacao: event.target.value }))}
              />
            </RBForm.Group>

            <InfoBox>
              <strong>Quantidade de notas na origem:</strong> {selectedEnderecoOrigem?.quantidadeNotas ?? 0}
              <InfoText>Ao confirmar, todas as notas do endereco origem serao movidas para o destino.</InfoText>
            </InfoBox>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleCloseMovimentacaoModal}>
              Cancelar
            </Button>
            <ButtonWithLoading type="button" variant="warning" onClick={handleSubmitMovimentacao} loading={movimentando}>
              Confirmar movimentacao
            </ButtonWithLoading>
          </Modal.Footer>
        </Modal>

        <Modal show={historicoModalOpen} onHide={handleCloseHistoricoModal} size="xl" centered>
          <Modal.Header closeButton>
            <Modal.Title>Historico de Movimentacoes</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <InfoBox className="mb-3">
              <strong>Endereco: 1.A.2.3.4</strong>
              <InfoText>Sem bloco: 1..2.3.4</InfoText>
            </InfoBox>
            <TwoColumns>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Armazem origem</RBForm.Label>
                <RBForm.Select
                  value={historicoFilters.idDepositoArmazemOrigem}
                  onChange={(event) => setHistoricoFilters((current) => ({ ...current, idDepositoArmazemOrigem: event.target.value }))}
                >
                  <option value="">Todos</option>
                  {armazemOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </RBForm.Select>
              </RBForm.Group>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Armazem destino</RBForm.Label>
                <RBForm.Select
                  value={historicoFilters.idDepositoArmazemDestino}
                  onChange={(event) => setHistoricoFilters((current) => ({ ...current, idDepositoArmazemDestino: event.target.value }))}
                >
                  <option value="">Todos</option>
                  {armazemOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </RBForm.Select>
              </RBForm.Group>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Endereco origem</RBForm.Label>
                <RBForm.Control
                  value={historicoFilters.enderecoOrigemTexto}
                  onChange={(event) => setHistoricoFilters((current) => ({ ...current, enderecoOrigemTexto: event.target.value }))}
                  placeholder="Ex: 1.A.2.3.4"
                />
              </RBForm.Group>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Endereco destino</RBForm.Label>
                <RBForm.Control
                  value={historicoFilters.enderecoDestinoTexto}
                  onChange={(event) => setHistoricoFilters((current) => ({ ...current, enderecoDestinoTexto: event.target.value }))}
                  placeholder="Ex: 1..2.3.4"
                />
              </RBForm.Group>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Usuario</RBForm.Label>
                <RBForm.Control
                  value={historicoFilters.usuario}
                  onChange={(event) => setHistoricoFilters((current) => ({ ...current, usuario: event.target.value }))}
                />
              </RBForm.Group>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Data inicial</RBForm.Label>
                <RBForm.Control
                  type="datetime-local"
                  value={historicoFilters.dataInicial}
                  onChange={(event) => setHistoricoFilters((current) => ({ ...current, dataInicial: event.target.value }))}
                />
              </RBForm.Group>
              <RBForm.Group className="mb-3">
                <RBForm.Label>Data final</RBForm.Label>
                <RBForm.Control
                  type="datetime-local"
                  value={historicoFilters.dataFinal}
                  onChange={(event) => setHistoricoFilters((current) => ({ ...current, dataFinal: event.target.value }))}
                />
              </RBForm.Group>
            </TwoColumns>

            <HistoryActions>
              <Button variant="outline-secondary" onClick={() => setHistoricoFilters(emptyHistoricoFilters)}>
                Limpar filtros
              </Button>
              <ButtonWithLoading type="button" variant="primary" onClick={carregarHistorico} loading={loadingHistorico}>
                Buscar
              </ButtonWithLoading>
            </HistoryActions>

            <HistoryTableWrap>
              <table className="table table-hover table-sm mb-0">
                <thead>
                  <tr>
                    <th>Origem</th>
                    <th>Destino</th>
                    <th>Usuario</th>
                    <th>Data/Hora</th>
                    <th>Qtd.</th>
                    <th>Observacao</th>
                  </tr>
                </thead>
                <tbody>
                  {historicoMovimentacoes.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-3">
                        Nenhuma movimentacao encontrada.
                      </td>
                    </tr>
                  )}
                  {historicoMovimentacoes.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => setSelectedHistoricoId(item.id)}
                      style={{ cursor: 'pointer', backgroundColor: item.id === selectedHistoricoId ? '#f5f7fa' : 'transparent' }}
                    >
                      <td>{formatEnderecoLabel(item.enderecoOrigem)}</td>
                      <td>{formatEnderecoLabel(item.enderecoDestino)}</td>
                      <td>{item.usuario || '-'}</td>
                      <td>{formatDateTime(item.dataHora)}</td>
                      <td>{item.quantidadeNotas || 0}</td>
                      <td><TruncatedText value={item.observacao || '-'} maxLength={40} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </HistoryTableWrap>

            {selectedHistorico && (
              <>
                <SectionTitle>Notas movidas</SectionTitle>
                <HistoryTableWrap>
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>NF</th>
                        <th>Local anterior</th>
                        <th>Local novo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedHistorico.itens || []).map((item) => (
                        <tr key={item.id}>
                          <td>{item.numeroNF || '-'}</td>
                          <td>{item.armazenagemLocalAnterior?.descricao || item.idDepositoArmazenagemLocalAnterior}</td>
                          <td>{item.armazenagemLocalNovo?.descricao || item.idDepositoArmazenagemLocalNovo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </HistoryTableWrap>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleCloseHistoricoModal}>
              Fechar
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={exclusaoLoteResumoOpen} onHide={handleCloseExclusaoLoteResumoModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Resumo da exclusao em lote</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SummaryList>
              <li>Solicitados: {exclusaoLoteResumo?.quantidadeSolicitada ?? 0}</li>
              <li>Processados: {exclusaoLoteResumo?.quantidadeProcessada ?? 0}</li>
              <li>Sucesso: {exclusaoLoteResumo?.quantidadeSucesso ?? 0}</li>
              <li>Erro: {exclusaoLoteResumo?.quantidadeErro ?? 0}</li>
            </SummaryList>

            <HistoryTableWrap>
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Endereco</th>
                    <th>Status</th>
                    <th>Mensagem</th>
                  </tr>
                </thead>
                <tbody>
                  {(exclusaoLoteResumo?.itens || []).map((item) => (
                    <tr key={`${item.id}-${item.descricao || ''}`}>
                      <td>{item.descricao || item.id}</td>
                      <td>{item.sucesso ? 'Sucesso' : 'Erro'}</td>
                      <td>{item.mensagem || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </HistoryTableWrap>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleCloseExclusaoLoteResumoModal}>
              Fechar
            </Button>
          </Modal.Footer>
        </Modal>
      </CardBody>
    </Card>
  );
};

export default EnderecamentoCard;

const HeaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
`;

const ActionsWrap = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const ActionButtonsWrap = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PreviewLabel = styled.span`
  font-weight: 600;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 240px;
  object-fit: contain;
  border: 1px solid #dfe3e8;
  border-radius: 6px;
  padding: 8px;
  background: #fff;
`;

const PreviewLink = styled.a`
  align-self: flex-start;
  font-weight: 600;
`;

const InfoBox = styled.div`
  border: 1px solid #dfe3e8;
  border-radius: 6px;
  padding: 12px;
  background: #f8fafc;
`;

const InfoText = styled.div`
  margin-top: 6px;
  color: #566074;
`;

const HistoryActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 16px;
`;

const HistoryTableWrap = styled.div`
  max-height: 280px;
  overflow: auto;
  border: 1px solid #e5e9f2;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h6`
  margin: 12px 0;
  font-weight: 600;
`;

const SummaryList = styled.ul`
  margin: 0 0 16px;
  padding-left: 18px;
`;
